import { zodResolver } from "@hookform/resolvers/zod";
import {
  roleHas,
  updateWorkspaceSchema,
  type UpdateWorkspaceInput,
  type WorkspaceDto,
} from "@iagenix/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { FormField } from "@/components/form-field";
import { PageHeader } from "@/components/page-header";
import { ErrorState, LoadingState } from "@/components/states";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, errorMessage } from "@/lib/api";
import { queries } from "@/lib/queries";
import { reportsApi } from "@/lib/reports-api";
import { ImageIcon, Trash2, UploadCloud } from "lucide-react";
import { useRef } from "react";

export const Route = createFileRoute("/app/$workspaceId/configuracoes")({
  component: ConfiguracoesPage,
});

function ConfiguracoesPage() {
  const { workspaceId } = Route.useParams();
  const workspace = useQuery(queries.workspace(workspaceId));

  if (workspace.isPending) return <LoadingState rows={4} />;
  if (workspace.isError)
    return <ErrorState error={workspace.error} onRetry={() => void workspace.refetch()} />;

  return (
    <>
      <PageHeader title="Configurações" description="Nome, identidade visual, plano e zona de perigo." />
      <div className="space-y-6">
        <DadosForm workspace={workspace.data} />
        {roleHas(workspace.data.role, "billing:read") ? (
          <Card>
            <CardHeader>
              <CardTitle>Plano e cobrança</CardTitle>
              <CardDescription>Plano atual, uso vs limites, assinatura, forma de pagamento e faturas.</CardDescription>
            </CardHeader>
            <CardFooter>
              <Button asChild variant="outline">
                <Link to="/app/$workspaceId/cobranca" params={{ workspaceId }}>
                  Abrir Plano e cobrança
                </Link>
              </Button>
            </CardFooter>
          </Card>
        ) : null}
        <LogoCard workspace={workspace.data} />
        {roleHas(workspace.data.role, "workspace:delete") ? (
          <ZonaDePerigo workspace={workspace.data} />
        ) : null}
      </div>
    </>
  );
}

function DadosForm({ workspace }: { workspace: WorkspaceDto }) {
  const queryClient = useQueryClient();
  const podeEditar = roleHas(workspace.role, "workspace:update");
  const form = useForm<UpdateWorkspaceInput>({
    resolver: zodResolver(updateWorkspaceSchema),
    defaultValues: { name: workspace.name, brandColor: workspace.brandColor ?? "#2563eb" },
  });

  const brandColor = useWatch({ control: form.control, name: "brandColor" });

  const salvar = useMutation({
    mutationFn: (input: UpdateWorkspaceInput) =>
      api<WorkspaceDto>(`/api/workspaces/${workspace.id}`, { method: "PATCH", body: input }),
    onSuccess: async (ws) => {
      await queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      form.reset({ name: ws.name, brandColor: ws.brandColor ?? "#2563eb" });
      toast.success("Configurações salvas.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Card>
      <form onSubmit={form.handleSubmit((v) => salvar.mutate(v))} noValidate>
        <CardHeader>
          <CardTitle>Dados do workspace</CardTitle>
          <CardDescription>
            A cor da marca vai aparecer nos relatórios para o cliente final.
          </CardDescription>
        </CardHeader>
        <CardContent className="mt-6 space-y-4">
          <FormField id="name" label="Nome" error={form.formState.errors.name?.message}>
            <Input id="name" disabled={!podeEditar} {...form.register("name")} />
          </FormField>
          <FormField
            id="brandColor"
            label="Cor da marca"
            error={form.formState.errors.brandColor?.message}
          >
            <div className="flex items-center gap-2">
              <input
                type="color"
                aria-label="Escolher cor"
                className="size-9 cursor-pointer rounded-md border bg-transparent p-0.5"
                disabled={!podeEditar}
                value={brandColor ?? "#2563eb"}
                onChange={(e) =>
                  form.setValue("brandColor", e.target.value, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
              />
              <Input
                id="brandColor"
                className="max-w-40 font-mono"
                disabled={!podeEditar}
                {...form.register("brandColor")}
              />
            </div>
          </FormField>
          <p className="text-xs text-muted-foreground">
            Identificador: <code className="font-mono">{workspace.slug}</code>
          </p>
        </CardContent>
        {podeEditar ? (
          <CardFooter className="mt-6 justify-end">
            <Button type="submit" loading={salvar.isPending} disabled={!form.formState.isDirty}>
              Salvar
            </Button>
          </CardFooter>
        ) : null}
      </form>
    </Card>
  );
}

/** Logo da agência (Fase 6): aparece no relatório público e no PDF. */
function LogoCard({ workspace }: { workspace: WorkspaceDto }) {
  const queryClient = useQueryClient();
  const podeEditar = roleHas(workspace.role, "workspace:update");
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useMutation({
    mutationFn: (file: File) => reportsApi.uploadLogo(workspace.id, file),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      toast.success("Logo atualizada.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: () => reportsApi.removeLogo(workspace.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      toast.success("Logo removida.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Logo da agência</CardTitle>
        <CardDescription>
          PNG ou JPEG até 2 MB. Aparece no relatório público para o cliente final, no PDF e nos
          e-mails agendados, junto com a cor da marca.
        </CardDescription>
      </CardHeader>
      <CardContent className="mt-6 flex flex-wrap items-center gap-4">
        <div
          className="flex h-20 w-40 items-center justify-center overflow-hidden rounded-md border bg-muted/40"
          style={{ borderColor: workspace.brandColor ?? undefined }}
        >
          {workspace.logoUrl ? (
            <img
              src={workspace.logoUrl}
              alt="Logo da agência"
              className="max-h-16 max-w-36 object-contain"
            />
          ) : (
            <ImageIcon className="size-6 text-muted-foreground" aria-hidden />
          )}
        </div>
        {podeEditar ? (
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg"
              className="hidden"
              aria-label="Escolher arquivo da logo"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) upload.mutate(file);
                e.target.value = "";
              }}
            />
            <Button
              variant="outline"
              loading={upload.isPending}
              onClick={() => inputRef.current?.click()}
            >
              <UploadCloud />
              {workspace.logoUrl ? "Trocar logo" : "Enviar logo"}
            </Button>
            {workspace.logoUrl ? (
              <Button
                variant="ghost"
                className="text-destructive"
                loading={remove.isPending}
                onClick={() => remove.mutate()}
              >
                <Trash2 />
                Remover
              </Button>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function ZonaDePerigo({ workspace }: { workspace: WorkspaceDto }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [aberto, setAberto] = useState(false);
  const [confirmacao, setConfirmacao] = useState("");

  const excluir = useMutation({
    mutationFn: () => api(`/api/workspaces/${workspace.id}`, { method: "DELETE" }),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: ["workspaces", workspace.id] });
      await queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      toast.success("Workspace excluído.");
      await navigate({ to: "/app" });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle>Excluir workspace</CardTitle>
        <CardDescription>
          Remove o acesso de todos os membros. Os dados ficam retidos para auditoria e podem ser
          apagados em definitivo a pedido (LGPD).
        </CardDescription>
      </CardHeader>
      <CardFooter>
        <Button variant="destructive" onClick={() => setAberto(true)}>
          Excluir este workspace
        </Button>
      </CardFooter>
      <Dialog open={aberto} onOpenChange={setAberto}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir {workspace.name}?</DialogTitle>
            <DialogDescription>
              Digite <strong>{workspace.slug}</strong> para confirmar.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={confirmacao}
            onChange={(e) => setConfirmacao(e.target.value)}
            placeholder={workspace.slug}
            aria-label="Confirmação"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAberto(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={confirmacao !== workspace.slug}
              loading={excluir.isPending}
              onClick={() => excluir.mutate()}
            >
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
